import { GridRowSelectionModel } from "@mui/x-data-grid";
import { Button, Alert, Fade, Box, Menu, Checkbox, FormControlLabel, Divider } from "@mui/material";
import { useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { ServicesDropdown } from "../../../../components/general-dropdowns/services-dropdown/ServicesDropdown";

type SelectAllFutureSessionsProps = {
    selectedRows: GridRowSelectionModel;
    setSelectedRows: (rows: GridRowSelectionModel) => void;
    items: Array<any>;
};

export const SelectAllFutureSessions = ({ selectedRows, setSelectedRows, items }: SelectAllFutureSessionsProps) => {
    const [localAlert, setLocalAlert] = useState("");
    const [showLocalAlert, setShowLocalAlert] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedServiceId, setSelectedServiceId] = useState<string>("");
    const [selectAllFuture, setSelectAllFuture] = useState(false);

    const isMenuOpen = Boolean(anchorEl);

    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const showAlert = (message: string) => {
        setLocalAlert(message);
        setShowLocalAlert(true);
        setTimeout(() => setShowLocalAlert(false), 3000);
    };

    const filterFutureBookedSessions = (serviceId?: string) => {
        const now = new Date();
        const filteredRows = items.filter((row: any) => {
            const startDate = new Date(row.start_date);
            const isAfterNow = startDate > now;
            const isBookedOrConfirmed = row.status === "booked";
            const matchesService = serviceId ? row.service?.id === Number(serviceId) : true;
            return isAfterNow && isBookedOrConfirmed && matchesService;
        });
        return filteredRows;
    };

    const handleSelectAllCheckboxChange = (checked: boolean) => {
        setSelectAllFuture(checked);
        if (checked) {
            setSelectedServiceId("");
        }
    };

    const handleServiceChange = (serviceId: string) => {
        setSelectedServiceId(serviceId);
        if (serviceId) {
            setSelectAllFuture(false);
        }
    };

    const handleApplySelection = () => {
        const serviceIdToFilter = selectAllFuture ? undefined : selectedServiceId;

        if (!selectAllFuture && !selectedServiceId) {
            showAlert("Selecione uma opção primeiro");
            return;
        }

        const filteredRows = filterFutureBookedSessions(serviceIdToFilter);

        if (filteredRows.length === 0) {
            const message = selectAllFuture
                ? "Nenhuma sessão futura encontrada no estado agendado"
                : "Nenhuma sessão futura encontrada para este serviço";
            showAlert(message);
            return;
        }

        const filteredRowIds = filteredRows.map((row: any) => row.id);
        setSelectedRows(filteredRowIds);
        handleCloseMenu();
    };

    const handleDeselectAll = () => {
        setSelectedRows([]);
    };

    const hasSelection = selectAllFuture || selectedServiceId;

    return (
        <>
            <Box sx={{ display: "flex", gap: 2 }}>
                {selectedRows.length > 0 && (
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ClearIcon />}
                        onClick={handleDeselectAll}
                        sx={{
                            boxShadow: 2,
                            borderRadius: 6,
                            textTransform: "none",
                        }}
                    >
                        Desmarcar todas
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowDropUpIcon />}
                    onClick={handleOpenMenu}
                    sx={{
                        boxShadow: 3,
                        borderRadius: 6,
                        textTransform: "none",
                    }}
                >
                    Seleccionar Agendadas
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={handleCloseMenu}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "bottom", horizontal: "right" }}
                    slotProps={{ paper: { sx: { minWidth: 280, mb: 1 } } }}
                >
                    <Box sx={{ px: 2, py: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectAllFuture}
                                    onChange={(e) => handleSelectAllCheckboxChange(e.target.checked)}
                                />
                            }
                            label="Todas as futuras"
                        />
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1 }} onClick={(e) => e.stopPropagation()}>
                        <ServicesDropdown
                            value={selectedServiceId}
                            onChange={handleServiceChange}
                            label="Filtrar por serviço"
                        />
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={handleApplySelection}
                            disabled={!hasSelection}
                        >
                            Selecionar
                        </Button>
                    </Box>
                </Menu>
            </Box>
            <Fade in={showLocalAlert}>
                <Alert
                    severity="info"
                    sx={{
                        position: "absolute",
                        bottom: 60,
                        right: 0,
                        maxWidth: 280,
                        fontSize: "0.875rem",
                        zIndex: 1000,
                    }}
                >
                    {localAlert}
                </Alert>
            </Fade>
        </>
    );
};
